import { useMemo } from "react";

import { omit } from "lodash";
import { Control, FieldValues } from "react-hook-form";

import { TextFieldProps } from "@mui/material";

import { HomeTypeName, OptionType } from "@covertree/types";

import {
  buildSelectableOptionsWithImageAndDescription,
  FormSelectField,
} from "../FormSelectField";
import { homeTypes } from "./utils";

type Props<TValue extends FieldValues> = Omit<TextFieldProps, "variant"> & {
  control: Control<TValue>;
  name?: string;
  label?: React.ReactNode;
  placeholder?: string;
  optional?: boolean;
  disabled?: boolean;
  hasUnknown?: boolean;
  fontSize?: number;
  labelFontSize?: number;
};

export const FormHomeTypeSelect = <TValue extends FieldValues>({
  control,
  name = "homeType",
  label = "Home Type",
  placeholder = "Select home type...",
  optional = false,
  disabled = false,
  hasUnknown = true,
  fontSize,
  labelFontSize,
}: Props<TValue>) => {
  const options = useMemo(
    () => (hasUnknown ? homeTypes : omit(homeTypes, "Unknown")),
    [hasUnknown]
  );

  const definitions = useMemo(
    () => buildSelectableOptionsWithImageAndDescription(options),
    [options]
  );

  return (
    <FormSelectField
      id="home-type"
      name={name}
      control={control}
      disabled={disabled}
      options={Object.entries(options).map<OptionType<HomeTypeName>>(
        ([key, value]) => ({
          label: value.name,
          value: key as HomeTypeName,
        })
      )}
      label={label}
      dataTestId="home-type-input"
      placeholder={placeholder}
      definition={definitions}
      optional={optional}
      fontSize={fontSize}
      labelFontSize={labelFontSize}
    />
  );
};
